// components/ProtectedRoute.jsx
import React from "react";
import { Navigate } from "react-router-dom";
import { Box, CircularProgress, Typography, Paper } from "@mui/material";
import { 
  Security as SecurityIcon,
  Shield as ShieldIcon,
} from "@mui/icons-material";
import { useAuthContext } from "../contexts/AuthContext";

/**
 * Protected Route Component
 * Handles authentication, profile completion, email verification, and role-based access
 */
const ProtectedRoute = ({ 
  children, 
  requiredRole, 
  requireProfileComplete = true,
  requireEmailVerification = true,
  redirectTo = "/auth/sign-in" 
}) => {
  const auth = useAuthContext();

  // Show loading state with Neumorphic design
  if (auth.loading) {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "100vh",
          gap: 3,
          bgcolor: "#e0e5ec",
        }}
      >
        <Box sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: 3,
        }}>
          {/* Neumorphic Loading Spinner Container */}
          <Box sx={{
            width: 80,
            height: 80,
            borderRadius: "50%",
            background: "#e0e5ec",
            boxShadow: "9px 9px 16px #b8bec6, -9px -9px 16px #ffffff",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            position: "relative",
          }}>
            <CircularProgress 
              size={50} 
              sx={{ 
                color: "#015580",
                '& .MuiCircularProgress-circle': {
                  strokeLinecap: 'round',
                }
              }} 
            />
            <Box sx={{
              position: "absolute",
              bottom: -8,
              right: -8,
              width: 32,
              height: 32,
              borderRadius: "50%",
              background: "#e0e5ec",
              boxShadow: "4px 4px 8px #b8bec6, -4px -4px 8px #ffffff",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}>
              <ShieldIcon sx={{ fontSize: 16, color: "#48BB78" }} />
            </Box>
          </Box>

          {/* Loading Text with Neumorphic Style */}
          <Paper
            elevation={0}
            sx={{
              px: 4,
              py: 1.5,
              borderRadius: 3,
              background: "#e0e5ec",
              boxShadow: "inset 3px 3px 7px #b8bec6, inset -3px -3px 7px #ffffff",
            }}
          >
            <Typography 
              variant="body2" 
              sx={{ 
                color: "#2c3e50",
                fontWeight: 500,
                letterSpacing: "0.02em",
              }}
            >
              <SecurityIcon sx={{ fontSize: 14, verticalAlign: "middle", mr: 1, color: "#015580" }} />
              Verifying Access...
            </Typography>
          </Paper>

          {/* Decorative Background Shapes */}
          <Box sx={{
            position: "fixed",
            top: "10%",
            left: "5%",
            width: "250px",
            height: "250px",
            borderRadius: "50%",
            background: "#e0e5ec",
            boxShadow: "12px 12px 24px #b8bec6, -12px -12px 24px #ffffff",
            opacity: 0.3,
            zIndex: 0,
            animation: "floatBg 8s ease-in-out infinite",
          }} />
          <Box sx={{
            position: "fixed",
            bottom: "10%",
            right: "5%",
            width: "200px",
            height: "200px",
            borderRadius: "50%",
            background: "#e0e5ec",
            boxShadow: "12px 12px 24px #b8bec6, -12px -12px 24px #ffffff",
            opacity: 0.3,
            zIndex: 0,
            animation: "floatBg 10s ease-in-out infinite reverse",
          }} />
        </Box>

        <style>
          {`
            @keyframes floatBg {
              0%, 100% { transform: translateY(0px) rotate(0deg); }
              50% { transform: translateY(-20px) rotate(5deg); }
            }
          `}
        </style>
      </Box>
    );
  }

  // Check if user is authenticated
  if (!auth.isAuthenticated) {
    return <Navigate to={redirectTo} replace />;
  }

  // Check if profile needs to be completed
  if (requireProfileComplete && auth.needsProfileCompletion) {
    return <Navigate to="/auth/complete-profile" replace />;
  }

  // Check if email is verified (only for email auth users)
  if (requireEmailVerification && auth.needsEmailVerification) {
    return <Navigate to="/verify-email" replace />;
  }

  // Check if user has required role
  if (requiredRole && auth.role !== requiredRole) {
    // Redirect to appropriate dashboard
    if (auth.isAdmin) {
      return <Navigate to="/admin/home" replace />;
    } else if (auth.isUser) {
      return <Navigate to="/user/home" replace />;
    } else {
      return <Navigate to="/" replace />;
    }
  }

  // All checks passed, render the protected component
  return children;
};

export default ProtectedRoute;